import type { ProductListItem } from "../types/Product";

// Client copy of the server "bestDeal" sort (server/pkg/handlers/product-browse-best-deal.go,
// pinto/utils/bestDealRanking.ts) for lists sorted in the browser, such as Saved.
// Percent off x sqrt(dollars saved), a lift for deals found in the last day, online
// near-100% markdowns damped, then categories spread across the first screens.

const DAY_MS = 24 * 60 * 60 * 1000;
const FRESH_BOOST = 1.15;
const SUSPICIOUS_PCT = 0.95;
const SUSPICIOUS_PENALTY = 0.1;
const DIVERSITY_HEAD = 60;
const DIVERSITY_WINDOW = 12;
const DIVERSITY_PER_CATEGORY = 2;

export function bestDealScore(p: ProductListItem, now: number = Date.now()): number {
  const msrp = Number(p.msrp) || 0;
  const price = p.storePrice?.[p.storePrice.length - 1] || 0;
  if (msrp <= 0 || price <= 0 || price >= msrp) return 0;
  const savings = msrp - price;
  const pct = savings / msrp;
  let score = pct * Math.sqrt(savings);
  const found = p.firstInserted || 0;
  if (found > 0 && now - found < DAY_MS) score *= FRESH_BOOST;
  if (pct >= SUSPICIOUS_PCT && p.storeId === "online") score *= SUSPICIOUS_PENALTY;
  return score;
}

const categoryKey = (p: ProductListItem) => (p.adjustedCategory || p.category || "").trim().toLowerCase();

export function diversifyByCategory<T extends ProductListItem>(sorted: T[]): T[] {
  if (sorted.length < 3) return sorted;
  const head: T[] = [];
  const pending: T[] = [];
  let next = 0;
  const fits = (item: T) => {
    const key = categoryKey(item);
    if (!key) return true;
    let seen = 0;
    for (let i = Math.max(0, head.length - DIVERSITY_WINDOW + 1); i < head.length; i++) {
      if (categoryKey(head[i]) === key) seen++;
    }
    return seen < DIVERSITY_PER_CATEGORY;
  };
  while (head.length < DIVERSITY_HEAD && (next < sorted.length || pending.length)) {
    const waiting = pending.findIndex(fits);
    if (waiting >= 0) {
      head.push(pending.splice(waiting, 1)[0]);
      continue;
    }
    if (next >= sorted.length) {
      head.push(pending.shift() as T);
      continue;
    }
    const candidate = sorted[next++];
    if (fits(candidate)) head.push(candidate);
    else pending.push(candidate);
  }
  return [...head, ...pending, ...sorted.slice(next)];
}

export function sortByBestDeal<T extends ProductListItem>(products: T[], now: number = Date.now()): T[] {
  const ranked = products
    .map((product) => ({ product, key: bestDealScore(product, now) }))
    .sort((a, b) => b.key - a.key)
    .map((entry) => entry.product);
  return diversifyByCategory(ranked);
}
